import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import { callSsoEndpoint, fetchSsoLoginStatus } from '@/api/sso';
import router from '@/router';
import { useDoorsaleStore } from '@/stores/doorsaleStore';
import { useGeneralStore } from '@/stores/general';
import type { SingleSignOnLoginStatus } from '@/types';
import { getSessionId } from '@/utils/getSessionId';
import { useWebsocketStore } from '@/websocket/websocket';

// Same key as the old client. It is set right before the login redirect so the basket can be re-priced when the user returns.
const PRICE_ADJUSTMENT_KEY = 'sso_price_adjustment';
const KEEP_ALIVE_MS = 30 * 60 * 1000;
const READY_TIMEOUT_MS = 3000;
// Login types the client can handle. 'credentials' (inlogic) is not implemented yet.
const SUPPORTED_LOGIN_TYPES = ['redirect'];

interface EventAccessInfo {
  single_sign_on_required?: boolean;
  single_sign_on_required_usertypes?: string[];
}

export const useSsoStore = defineStore('sso', () => {
  const generalStore = useGeneralStore();

  // 'unknown' until init() has finished, also when the customer turns out to have no sso.
  const initState = ref<'unknown' | 'ready'>('unknown');
  const loginStatus = ref<SingleSignOnLoginStatus | null>(null);

  let initStarted = false;
  let repricePending = false;
  let keepAliveTimer: number | undefined;
  let resolveReady: () => void = () => {};
  const readyPromise = new Promise<void>(resolve => {
    resolveReady = resolve;
  });

  const isActive = computed(() => {
    const customer = generalStore.customer();
    return !!customer.single_sign_on_active && !!customer.single_sign_on_status_url;
  });
  const isLoading = computed(() => initState.value === 'unknown');
  const isLoggedIn = computed(() => loginStatus.value?.login_status === 'loggedin');
  // The login button is only shown when the client knows how to log in with the customer's sso.
  const isAvailable = computed(() => {
    const loginType = loginStatus.value?.login_type;
    return isActive.value && !!loginType && SUPPORTED_LOGIN_TYPES.includes(loginType);
  });
  const usertype = computed(() => (isLoggedIn.value ? loginStatus.value?.usertype ?? null : null));
  const userName = computed(() => (isLoggedIn.value ? loginStatus.value?.name ?? '' : ''));
  const userEmail = computed(() => (isLoggedIn.value ? loginStatus.value?.email ?? '' : ''));
  const hasExclusiveMessage = computed(() => isLoggedIn.value && !!loginStatus.value?.has_exclusive_message);
  // The usertype whose sso price is shown next to the normal price. Set per sso in the backend.
  const memberUsertype = computed(() => generalStore.customer().single_sign_on_member_usertype ?? null);

  // Resolves when init() is done. Never waits longer than the timeout, so a slow sso never blocks the shop.
  function whenReady(timeoutMs: number = READY_TIMEOUT_MS): Promise<void> {
    return Promise.race([
      readyPromise,
      new Promise<void>(resolve => setTimeout(resolve, timeoutMs)),
    ]);
  }

  function canAccessEvent(event: EventAccessInfo): boolean {
    if (!isActive.value || !event.single_sign_on_required) {
      return true;
    }
    if (!isLoggedIn.value) {
      return false;
    }
    // Plugins without usertypes (inlogic) only require a login.
    if (!usertype.value) {
      return true;
    }
    return (event.single_sign_on_required_usertypes ?? []).includes(usertype.value);
  }

  async function init() {
    if (initStarted) {
      return;
    }
    initStarted = true;

    try {
      const statusUrl = generalStore.customer().single_sign_on_status_url;
      if (isActive.value && statusUrl) {
        const status = await fetchSsoLoginStatus(statusUrl);
        if (status) {
          loginStatus.value = status;
          if (status.login_status === 'loggedin' && sessionStorage.getItem(PRICE_ADJUSTMENT_KEY) === 'true') {
            repricePending = true;
          }
          await handleLogoutReturn();
        }
      }
    } catch (e) {
      console.error('Failed to initialize sso:', e);
    } finally {
      initState.value = 'ready';
      resolveReady();
    }

    if (isLoggedIn.value) {
      startKeepAlive();
    }
    syncWebsocket();
  }

  // The provider logout page returns to /?logged_out=true. Then the sso state in the shop session must be cleared too.
  async function handleLogoutReturn() {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('logged_out')) {
      return;
    }
    await clearSession();

    await router.isReady();
    const route = router.currentRoute.value;
    const query = Object.fromEntries(Object.entries(route.query).filter(([key]) => key !== 'logged_out'));
    await router.replace({ path: route.path, query, hash: route.hash });
  }

  async function clearSession() {
    const status = loginStatus.value;
    if (!status) {
      return;
    }
    if (status.session_logout_url) {
      await callSsoEndpoint(status.session_logout_url);
    }
    loginStatus.value = {
      ...status,
      login_status: 'loggedout',
      name: '',
      email: null,
      usertype: null,
      userid: '',
      has_exclusive_message: 0,
      logout_url: '',
    };
    stopKeepAlive();
    // The basket may contain member prices from the login.
    sessionStorage.removeItem(PRICE_ADJUSTMENT_KEY);
    repricePending = true;
    syncWebsocket();
  }

  function login() {
    const status = loginStatus.value;
    if (!isAvailable.value || !status?.login_url) {
      return;
    }
    sessionStorage.setItem(PRICE_ADJUSTMENT_KEY, 'true');
    window.location.assign(status.login_url);
  }

  async function logout() {
    const status = loginStatus.value;
    if (!status || !isLoggedIn.value) {
      return;
    }
    if (status.logout_url) {
      // The provider logs the user out and sends the browser back to /?logged_out=true
      window.location.assign(status.logout_url);
      return;
    }
    await clearSession();
  }

  function startKeepAlive() {
    stopKeepAlive();
    const refreshUrl = loginStatus.value?.refresh_url;
    if (!refreshUrl) {
      return;
    }
    keepAliveTimer = window.setInterval(() => {
      callSsoEndpoint(refreshUrl);
    }, KEEP_ALIVE_MS);
  }

  function stopKeepAlive() {
    if (keepAliveTimer !== undefined) {
      window.clearInterval(keepAliveTimer);
      keepAliveTimer = undefined;
    }
  }

  // The websocket server stores the sso status on the rtoken and uses it to find member prices.
  // It does not trust the usertype sent here. It reads the session itself, using the session id.
  function getWebsocketStatusData() {
    const status = loginStatus.value;
    if (!isActive.value || !status) {
      return null;
    }
    const session = getSessionId();
    if (status.login_status === 'loggedin' && !session) {
      console.warn('sso: the sessionid cookie is not readable, the server will treat the user as logged out');
    }
    return {
      status: status.login_status,
      name: status.name,
      email: status.email,
      usertype: status.usertype,
      session,
    };
  }

  // Sent right before every event subscription (done by the websocket store).
  function sendLoginStatus() {
    if (useDoorsaleStore().isActive) {
      return;
    }
    const data = getWebsocketStatusData();
    if (data) {
      useWebsocketStore().send('ssologinstatus', data, undefined);
    }
  }

  // Called when the status becomes known or changes, and by the websocket store when it connects.
  function syncWebsocket() {
    if (useDoorsaleStore().isActive) {
      return;
    }
    const websocketStore = useWebsocketStore();
    if (!websocketStore.isConnected()) {
      return;
    }
    sendLoginStatus();
    if (repricePending && getWebsocketStatusData()) {
      repricePending = false;
      sessionStorage.removeItem(PRICE_ADJUSTMENT_KEY);
      websocketStore.send('ssologinbasketupdate', {}, undefined);
    }
  }

  return {
    initState,
    loginStatus,
    isActive,
    isLoading,
    isLoggedIn,
    isAvailable,
    usertype,
    userName,
    userEmail,
    hasExclusiveMessage,
    memberUsertype,
    whenReady,
    canAccessEvent,
    init,
    login,
    logout,
    sendLoginStatus,
    syncWebsocket,
  };
});
